import React, { useState, useEffect } from 'react';
import { CheckCircle, ArrowRight, ArrowLeft, ChevronDown, ChevronRight } from 'lucide-react';
import { TipsSection } from '../Common/TipsSection';

interface SetupARRProps {
  onComplete: (selectedRevenueOptions: string[], selectedChurnOptions: string[]) => void;
  loadedRevenueOption?: string;
  loadedArrMethod?: string;
  loadedChurnOptions?: string[];
}

const DEFAULT_ARR_METHOD = 'Last month MRR * 12';
const DEFAULT_REVENUE_OPTION = 'january';
const DEFAULT_CHURN_OPTIONS = ['subscription_cancelled'];

interface QuestionConfig {
  id: string;
  number: number;
  title: string;
  description: string;
  type: 'checkbox' | 'dropdown';
  options: { id: string, value: string }[];
  defaultValue?: string | string[];
  defaultText?: string;
  gridCols?: number;
  required?: boolean;
}

interface MetricConfig {
  id: string;
  label: string;
  labelBgColor: string;
  labelTextColor: string;
  title: string;
  description: string;
  isCompleted: boolean;
  isActive: boolean;
  questions: QuestionConfig[];
  tip?: {
    title: string;
    content: React.ReactNode;
    icon?: React.ReactNode;
    type?: "success" | "info";
    timeout?: number;
  };
}

export const SetupARR: React.FC<SetupARRProps> = ({
  onComplete,
  loadedRevenueOption,
  loadedArrMethod,
  loadedChurnOptions
}) => {
  const [selectedArrMethod, setSelectedArrMethod] = useState<string>(DEFAULT_ARR_METHOD);
  const [selectedRevenueOption, setSelectedRevenueOption] = useState<string>(DEFAULT_REVENUE_OPTION);
  const [selectedChurnOptions, setSelectedChurnOptions] = useState<string[]>(DEFAULT_CHURN_OPTIONS);

  const [activeMetric, setActiveMetric] = useState<string>('total_revenue'); 
  const [continuedMetrics, setContinuedMetrics] = useState<Set<string>>(new Set());

  const loadedRevenueOptionSet = React.useRef(false);
  const loadedArrMethodSet = React.useRef(false);
  const loadedChurnOptionsSet = React.useRef(false);

  function arraysEqual(a?: string[], b?: string[]): boolean {
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((v, i) => v === sortedB[i]);
  }

  useEffect(() => {
    const allMetricsIds = metricsConfig.map(m => m.id);
    const allContinued = allMetricsIds.every(id => continuedMetrics.has(id));

    if (allContinued) {
      onComplete([selectedRevenueOption, selectedArrMethod], selectedChurnOptions);
    }
  }, [selectedRevenueOption, selectedArrMethod, selectedChurnOptions, continuedMetrics]);

  useEffect(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const stepParam = urlParams.get('step');
      const substepParam = urlParams.get('substep');
      const parsedStep = stepParam ? parseInt(stepParam, 10) : NaN;
      const parsedSubstep = substepParam ? parseInt(substepParam, 10) : NaN;

      if (parsedStep !== 4 || Number.isNaN(parsedSubstep)) return;

      if (parsedSubstep === 1) {
        setActiveMetric('total_revenue');
      } else if (parsedSubstep === 2) {
        setActiveMetric('arr');
      } else if (parsedSubstep === 3) {
        setActiveMetric('churn');
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (
      !loadedRevenueOptionSet.current &&
      loadedRevenueOption !== undefined &&
      loadedRevenueOption !== selectedRevenueOption
    ) {
      setSelectedRevenueOption(loadedRevenueOption);
      loadedRevenueOptionSet.current = true;
    }
    if (
      !loadedArrMethodSet.current &&
      loadedArrMethod !== undefined &&
      loadedArrMethod !== selectedArrMethod
    ) {
      setSelectedArrMethod(loadedArrMethod);
      loadedArrMethodSet.current = true;
    }
    if (
      !loadedChurnOptionsSet.current &&
      loadedChurnOptions &&
      !arraysEqual(loadedChurnOptions, selectedChurnOptions)
    ) {
      setSelectedChurnOptions(loadedChurnOptions);
      loadedChurnOptionsSet.current = true;
    }
  }, [loadedRevenueOption, loadedArrMethod, loadedChurnOptions]);


  const arrCalculationMethods = [
    'Last month MRR * 12',
    'Average MRR for fiscal year * 12'
  ];


  const renderQuestionContent = (question: QuestionConfig) => {
    const getOptions = () => {
      return question.options;
    };

    const getValue = () => {
      switch (question.id) {
        case 'arr_method':
          return selectedArrMethod;
        case 'total_revenue':
          return selectedRevenueOption;
        case 'churn_config':
          return selectedChurnOptions;
        default:
          return question.defaultValue || [];
      }
    };

    const handleChange = (value: string | string[]) => {
      switch (question.id) {
        case 'arr_method':
          setSelectedArrMethod(typeof value === 'string' ? value : value[0] || '');
          break;
        case 'total_revenue':
          setSelectedRevenueOption(typeof value === 'string' ? value : value[0] || '');
          break;
        case 'churn_config':
          setSelectedChurnOptions(Array.isArray(value) ? value : [value]);
          break;
      }
    };

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 overflow-y-auto">
        <h3 className="font-semibold text-gray-900 mb-2">{question.number}. {question.title}</h3>
        <p className="text-sm text-gray-600 mb-4 ml-4">{question.description}</p>

        {question.type === 'checkbox' && (
          <div className={`grid grid-cols-1 md:grid-cols-${question.gridCols || 3} gap-3`}>
            {getOptions().map(option => (
              <label key={option.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer rounded-lg">
                <input
                  type="checkbox"
                  checked={Array.isArray(getValue()) ? getValue().includes(option.id) : false}
                  onChange={e => {
                    const currentValue = getValue();
                    const currentArray = Array.isArray(currentValue) ? currentValue : [];
                    const newValue = e.target.checked
                      ? [...currentArray, option.id]
                      : currentArray.filter((item: string) => item !== option.id);
                    handleChange(newValue);
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">{option.value}</span>
              </label>
            ))}
          </div>
        )}

        {question.type === 'dropdown' && (
          <div className="relative">
            <select
              value={getValue() as string}
              onChange={e => handleChange(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
            >
              {getOptions().map(option => (
                <option key={option.id} value={option.id}>{option.value}</option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </div>
          </div>
        )}

        {question.defaultText && (
          <p className="text-xs text-gray-500 mt-3">{question.defaultText}</p>
        )}
      </div>
    );
  };


  const revenueQuestions: QuestionConfig[] = [
    {
      id: 'total_revenue',
      number: 1,
      title: 'When does your fiscal year start?',
      description: 'Set up your fiscal year start to enable precise total revenue calculations, aligned with your financial reporting cycle.',
      type: 'dropdown',
      options: [
        { id: 'january', value: 'January' },
        { id: 'february', value: 'February' },
        { id: 'march', value: 'March' },
        { id: 'april', value: 'April' },
        { id: 'may', value: 'May' },
        { id: 'june', value: 'June' },
        { id: 'july', value: 'July' },
        { id: 'august', value: 'August' },
        { id: 'september', value: 'September' },
        { id: 'october', value: 'October' },
        { id: 'november', value: 'November' },
        { id: 'december', value: 'December' }
      ],
      gridCols: 3
    }
  ];


  const churnQuestions: QuestionConfig[] = [
    {
      id: 'churn_config',
      number: 1,
      title: 'Set your fiscal year to track revenue accurately.',
      description: 'Select the conditions that define customer churn',
      type: 'checkbox',
      options: [
        { id: 'subscription_cancelled', value: 'Subscription Cancelled' },
        { id: 'end_of_period_with_no_payment', value: 'Failed Payment At End of Period' }
      ],
      gridCols: 2
    }
  ];

  const isArrCompleted = selectedArrMethod !== '';
  const isTotalRevenueCompleted = selectedRevenueOption !== '';
  const isChurnCompleted = selectedChurnOptions.length > 0;


  const metricsConfig: MetricConfig[] = [
    {
      id: 'total_revenue',
      label: 'Total Revenue',
      labelBgColor: '#dbeafe',
      labelTextColor: '#1e40af',
      title: 'Total Revenue Setup',
      description: 'Easily configure how your revenue is tracked and recognized',
      isCompleted: isTotalRevenueCompleted,
      isActive: activeMetric === 'total_revenue',
      questions: revenueQuestions,
      tip: {
        title: "What is Total Revenue?",
        content: (
          <div className="text-sm text-amber-800 flex flex-col gap-2">
            <div className="bg-amber-50 p-3 text-amber-900">
              <span className="font-medium"></span> Total Revenue represents all income generated by your business, including both recurring and one-time revenue streams.
            </div>
            <div className="ml-4">
              <strong>Includes:</strong><br />
              • Recurring subscription fees<br />
              • One-time purchases<br />
              • Setup fees and onboarding charges<br />
              • Professional services revenue<br />
              • Add-on products and upgrades
            </div>
            <div className="mt-2 ml-4">
              <strong>Fiscal Year Start:</strong><br />
              Choose which month marks the beginning of your fiscal year. This affects how revenue is calculated and reported for tax and accounting purposes.
            </div>
            <div className="mt-2 ml-4">
              <strong>Why it matters:</strong><br />
              Total Revenue gives you a complete picture of your business performance, helping with financial planning, investor reporting, and growth analysis.
            </div>
          </div>
        ),
        icon: <CheckCircle className="w-4 h-4 text-amber-600 mr-2" />,
        type: "info",
        timeout: 2000
      }
    },
    {
      id: 'arr',
      label: 'ARR',
      labelBgColor: '#dbeafe',
      labelTextColor: '#1e40af',
      title: 'ARR Configuration',
      description: 'Configure your ARR calculation',
      isCompleted: isArrCompleted,
      isActive: activeMetric === 'arr',
      questions: [
        {
          id: 'arr_method',
          number: 1,
          title: 'Select ARR Calculation Formula',
          description: 'Choose how you want to calculate your Annual Recurring Revenue (ARR).',
          type: 'dropdown',
          options: arrCalculationMethods.map((method, index) => ({ id: method, value: method })),
          defaultValue: 'Last month ARR * 12',
          required: true
        }
      ],
      tip: {
        title: "What is Annual Recurring Revenue (ARR)?",
        content: (
          <div className="text-sm text-amber-800 flex flex-col gap-2">
            <div className="bg-amber-50 p-3 text-amber-900">
              <span className="font-medium"></span> ARR is the annualized version of your Monthly Recurring Revenue (MRR). It represents the predictable, recurring revenue you can expect from your customers over a 12-month period.
            </div>
            <div className="ml-4">
              <strong>Formula:</strong><br />
              <span className="bg-white border border-amber-200 rounded px-2 py-1 inline-block mt-1">ARR = MRR × 12</span>
            </div>
            <div className="mt-2 ml-4">
              <strong>Why ARR matters:</strong><br />
              ARR helps you forecast annual revenue, plan budgets, and communicate with investors. It's a key metric for SaaS businesses and subscription models.
            </div>
            <div className="mt-2 ml-4">
              <strong>Calculation Methods:</strong><br />
              • <strong>Last month MRR × 12:</strong> Uses your most recent MRR<br />
              • <strong>Fiscal Monthly Average MRR × 12:</strong> Uses average MRR over time<br />
            </div>
          </div>
        ),
        icon: <CheckCircle className="w-4 h-4 text-amber-600 mr-2" />,
        type: "info",
        timeout: 2000
      }
    },
    
    // {
    //   id: 'churn',
    //   label: 'Churn',
    //   labelBgColor: '#dc2626',
    //   labelTextColor: '#ffffff',
    //   title: 'Customer Retention Metrics Configuration',
    //   description: 'Configure churn recognition and retention tracking',
    //   isCompleted: isChurnCompleted,
    //   isActive: activeMetric === 'churn',
    //   questions: churnQuestions,
    //   tip: {
    //     title: "What is Customer Churn?",
    //     content: (
    //       <div className="text-sm text-amber-800 flex flex-col gap-2">
    //         <div className="bg-amber-50 p-3 text-amber-900">
    //           <span className="font-medium"></span> Customer Churn occurs when a customer stops using your product or service, either by canceling their subscription or not renewing.
    //         </div>
    //         <div className="ml-4">
    //           <strong>Churn Rate Formula:</strong><br />
    //           <span className="bg-white border border-amber-200 rounded px-2 py-1 inline-block mt-1">Churn Rate = (Churned Customers ÷ Total Customers) × 100</span>
    //         </div>
    //         <div className="mt-2 ml-4">
    //           <strong>Churn Types:</strong><br />
    //           • <strong>Subscription Cancelled:</strong> Customer actively cancels their subscription<br />
    //           • <strong>Failed Payment:</strong> Customer's payment fails and they don't update payment method<br />
    //           • <strong>End of Period:</strong> Customer doesn't renew after trial or billing period ends
    //         </div>
    //         <div className="mt-2 ml-4">
    //           <strong>Why tracking churn matters:</strong><br />
    //           Understanding churn helps you identify product issues, improve customer success, and predict revenue impact. Lower churn means higher customer lifetime value.
    //         </div>
    //       </div>
    //     ),
    //     icon: <CheckCircle className="w-4 h-4 text-amber-600 mr-2" />,
    //     type: "info",
    //     timeout: 2000
    //   }
    // }
  ];


  const renderMetricContent = (metric: MetricConfig) => (
    <div className="space-y-6">
      {/* Render tip section if available */}
      {metric.tip && (
        <div className="mb-4">
          <TipsSection
            title={metric.tip.title}
            content={metric.tip.content}
            icon={metric.tip.icon}
            type={metric.tip.type}
            timeout={metric.tip.timeout}
          />
        </div>
      )}

      {metric.questions.map(question => (
        <div key={question.id}>
          {renderQuestionContent(question)}
        </div>
      ))}

      {/* Continue Button */}
      <div className="flex justify-end">
        <button
          onClick={() => {
            if (metric.isCompleted) {
              setContinuedMetrics(prev => {
                const next = new Set<string>();
                prev.forEach((v) => next.add(v));
                next.add(metric.id);
                return next;
              });

              const currentIndex = metricsConfig.findIndex(m => m.id === metric.id);
              if (currentIndex !== -1 && currentIndex + 1 < metricsConfig.length) {
                setActiveMetric(metricsConfig[currentIndex + 1].id);
              } else {
                setActiveMetric('');
              }
            }
          }}
          disabled={!metric.isCompleted}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors ${!metric.isCompleted
              ? 'bg-gray-400 cursor-not-allowed opacity-60'
              : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
        >
          <span>Continue</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="ms-9 mt-5">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {metricsConfig.map((metric, idx) => (
          <div key={metric.id} className={`border-b border-gray-200 last:border-b-0 ${metric.isActive ? 'bg-blue-50' : ''}`}>
            <button
              type="button"
              onClick={() => setActiveMetric(metric.isActive ? '' : metric.id)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full border-2 bg-blue-500 text-white font-semibold text-sm">
                  {idx + 1}
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{metric.title}</div>
                  <div className="text-sm text-gray-500">{metric.description}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {(() => {
                  if (metric.id === 'arr') {
                    return selectedArrMethod === DEFAULT_ARR_METHOD ? (
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">Default</span>
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    );
                  }
                  if (metric.id === 'total_revenue') {
                    return selectedRevenueOption === DEFAULT_REVENUE_OPTION ? (
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">Default</span>
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    );
                  }
                  if (metric.id === 'churn') {
                    return arraysEqual(selectedChurnOptions, DEFAULT_CHURN_OPTIONS) ? (
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">Default</span>
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    );
                  }
                  return null;
                })()}

                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${metric.isActive ? 'rotate-180' : ''}`} />
              </div>
            </button>
            {metric.isActive && (
              <div className="border-t border-gray-200 p-6 bg-white">
                {renderMetricContent(metric)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SetupARR;
